import { useEffect, useRef, useState } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Calendar, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { business } from "../data/business";
import { HeroVideo } from "./HeroVideo";
import { fireToast } from "./Toast";

gsap.registerPlugin(ScrollTrigger);

/**
 * Cinematic hero: full-bleed looped video with all headline copy
 * burned into the video itself. The page only renders the CTA pair.
 */
export function Hero() {
  const root = useRef<HTMLElement>(null);
  // Mobile round 2 — item #3: fade the "SCROLL" chevron once the user has
  // scrolled past the hero so it doesn't chase them down the page. rAF-
  // throttled passive listener keeps this off the scroll hot path.
  const [scrolledPast, setScrolledPast] = useState(false);
  useEffect(() => {
    let ticking = false;
    const update = () => {
      ticking = false;
      setScrolledPast(window.scrollY > 200);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useGSAP(
    () => {
      const reduce =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (reduce) return;

      // CTAs animate in subtly so the video has space to breathe first.
      gsap.from(".hero-cta-stack", {
        y: 28,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        delay: 0.6,
      });
      gsap.from(".hero-scroll", {
        opacity: 0,
        duration: 0.6,
        ease: "power3.out",
        delay: 1.4,
      });

      // Slow parallax on the video while scrolling out of view.
      gsap.to(".hero-bg", {
        yPercent: 12,
        ease: "none",
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });

      // Cinematic fade-to-black as user scrolls past the hero.
      gsap.to(".hero-tint", {
        opacity: 0.85,
        ease: "none",
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });
    },
    { scope: root },
  );

  return (
    <section ref={root} className="hero hero-cinema">
      <div className="hero-bg">
        <HeroVideo
          src="/videos/rafting-hero.mp4?v=9"
          srcMobile="/videos/rafting-hero-mobile.mp4?v=9"
          poster="/images/generated-1777507384714.png"
          className="hero-bg-video"
        />
      </div>
      <div className="hero-tint" aria-hidden="true" />

      {/* The video carries all the headline copy. Only CTAs live in the DOM. */}
      {/* Polish round 2 — item #15: explicit visual hierarchy confirmed.
          Primary action ("Book Now") uses .btn-gold (filled gold pill).
          Secondary ("View Trips & Pricing") uses .btn-ghost (outlined). The
          order also reflects priority — Book first, browse second. */}
      <div className="hero-cta-stack">
        <div className="cta-row">
          <a
            href={business.bookingBase}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-gold btn-watch btn-hero"
            aria-label="Book now on TheFlyBook (opens in a new tab)"
            onClick={() => fireToast("Opening booking page…")}
          >
            <Calendar aria-hidden="true" /> Book Now <ExternalLink size={14} aria-hidden="true" />
          </a>
          <Link to="/rafting" className="btn btn-ghost btn-watch btn-hero">
            View Trips &amp; Pricing
          </Link>
        </div>
      </div>

      <span
        className={`hero-scroll${scrolledPast ? " is-scrolled-past" : ""}`}
        aria-hidden="true"
      >
        SCROLL
        <span className="chevron" />
      </span>
    </section>
  );
}
